import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';

import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';
import { ClientService } from '@/services/client.service';
import { AppliactionService } from '@/services/appliaction.servicee';
import { CustomerService } from '@/services/customer.service';
import { BreadcrumbService, CrumbItem } from '@/services/breadcrumb.service';
import { getProcessTypeLabel } from '@/pages/process.enum';
import { LayoutService } from '@/layout/service/layout.service';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { LoginService } from '@/services/login.service';
import { canGoBack, navigateBack } from '@/layout/route-context';

interface Breadcrumb {
    label: string;
    url?: string;
}

@Component({
    selector: '[app-breadcrumb]',
    standalone: true,
    imports: [CommonModule, ButtonModule, TooltipModule],
    template: `<nav class="layout-breadcrumb">
        <div *ngIf="shouldShowBackButton()" class="breadcrumb-back-btn">
            <p-button [rounded]="true" styleClass="topbar-btn" (click)="goBack()" pTooltip="Back" tooltipPosition="bottom" [appendTo]="'body'" [tooltipZIndex]="'20000'">
                <a rounded="true" text="true">
                    <i class="pi pi-arrow-left"></i>
                </a>
            </p-button>
        </div>
        <ol>
            <ng-template ngFor let-item let-first="first" [ngForOf]="breadcrumbs$ | async">
                <li *ngIf="!first" class="breadcrumb-separator"><span class="pi pi-chevron-right"></span></li>
                <li class="font-medium">
                    <span>{{ item.label }}</span>
                </li>
            </ng-template>
        </ol>
    </nav> `
})
export class AppBreadcrumb {
    private readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);

    readonly breadcrumbs$ = this._breadcrumbs$.asObservable();

    private readonly idpWorkflowHierarchy = [
        { path: 'idp-customer', label: 'IDP Client' },
        { path: 'idp-application', label: 'IDP Application' },
        { path: 'idp-cust', label: 'IDP Customer' },
        { path: 'idp-customer-process', label: 'IDP Customer Process' },
        { path: 'transaction-logger', label: 'Transaction Logger' },
        { path: 'document-summary', label: 'Document Summary' },
        { path: 'process-doc-extraction', label: 'Process Document Extraction' },
        { path: 'idp-process', label: 'IDP Document Type' },
        { path: 'idp-doc-grid', label: 'IDP Document List' },
        { path: 'idp-extraction', label: 'IDP Extraction' }
    ];

    private displayNames = {
        clientName: null as string | null,
        applicationName: null as string | null,
        customerName: null as string | null,
        documentType: null as string | null,
        extractionFile: null as string | null,
        processName: null as string | null
    };

    constructor(
        private router: Router,
        private clientService: ClientService,
        private applicationService: AppliactionService,
        private customerService: CustomerService,
        private breadcrumbService: BreadcrumbService,
        public layoutService: LayoutService,
        public loginService: LoginService
    ) {
        // Prefer a custom breadcrumb trail if provided by a component
        this.breadcrumbService.trail$.subscribe((trail: CrumbItem[] | null) => {
            if (trail && trail.length) {
                this._breadcrumbs$.next(trail as Breadcrumb[]);
            }
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => this.buildTrail());

        // This component is created during the layout's first change detection,
        // which runs *after* the router has emitted NavigationEnd for the initial
        // navigation. Without this call, a direct hit or a refresh on any deep
        // link leaves the band empty until the next navigation.
        this.buildTrail();
    }

    private async buildTrail() {
        // If a custom trail is active, do not override it
        const custom = (this as any).breadcrumbService?._trail$?.getValue?.() as CrumbItem[] | null;
        if (custom && custom.length) return;

        const currentUrl = this.router.url;
        const segments = this.getPrimarySegments(currentUrl);
        const queryParams = this.router.parseUrl(currentUrl).queryParams || {};
        const routeParams = this.collectRouteParams();

        const isHomeRoot = segments.length === 1 && segments[0] === 'home';
        if (isHomeRoot) {
            this._breadcrumbs$.next([]);
            return;
        }

        // Reset display names each navigation; will be repopulated from API/params
        this.displayNames = {
            clientName: null,
            applicationName: null,
            customerName: null,
            documentType: null,
            extractionFile: null,
            processName: null
        };

        await this.populateDisplayNames(segments, queryParams);

        if (segments.includes('dashboard') || segments.includes('opex-dashboard')) {
            const breadcrumbs: Breadcrumb[] = [];

            if (this.displayNames.clientName) {
                breadcrumbs.push({ label: this.displayNames.clientName });
            }

            breadcrumbs.push({ label: 'Dashboard' });

            this._breadcrumbs$.next(breadcrumbs);
            return;
        }

        if (segments.includes('gemini-usage')) {
            const breadcrumbs: Breadcrumb[] = [];

            if (this.displayNames.clientName) {
                breadcrumbs.push({ label: this.displayNames.clientName });
            }

            breadcrumbs.push({ label: 'Gemini Usage' });

            this._breadcrumbs$.next(breadcrumbs);
            return;
        }

        if (segments.includes('idp-doc-grid')) {
            const clientId = this.loginService.getClientId();

            const breadcrumbs: Breadcrumb[] = [];

            // Client breadcrumb
            if (this.displayNames.clientName) {
                breadcrumbs.push({
                    label: this.displayNames.clientName
                    // url: `/extraction/idp-application/${clientId}`
                });
            }

            // File List breadcrumb
            breadcrumbs.push({
                label: 'File List'
            });

            this._breadcrumbs$.next(breadcrumbs);
            return;
        }

        const breadcrumbs: Breadcrumb[] = [];

        // Administration & Masterdata sections: show Client > Section > Page
        const isAdministration = segments.includes('administration');
        const isMasterdata = segments.includes('masterdata');
        if (isAdministration || isMasterdata) {
            const sectionLabel = isAdministration ? 'Administration' : 'Master Data';
            const leafLabel = this.getDeepestRouteLabel(this.router.routerState.snapshot.root) || sectionLabel;

            // For Administration and Masterdata, do NOT provide clickable URLs
            if (this.displayNames.clientName) {
                breadcrumbs.push({ label: this.displayNames.clientName });
            }
            breadcrumbs.push({ label: sectionLabel });
            if (leafLabel && leafLabel !== sectionLabel) {
                breadcrumbs.push({ label: leafLabel });
            }

            this._breadcrumbs$.next(breadcrumbs);
            return;
        }
        const currentPageIndex = this.idpWorkflowHierarchy.findIndex((item) => segments.includes(item.path));

        // Special breadcrumb for idp-extraction
        if (segments.includes('idp-extraction')) {
            const clientId = this.loginService.getClientId();
            const fileId = queryParams['txn'] ?? null;

            const breadcrumbs: Breadcrumb[] = [];

            if (this.displayNames.clientName) {
                breadcrumbs.push({
                    label: this.displayNames.clientName,
                    url: `/extraction/idp-application/${clientId}`
                });
            }

            breadcrumbs.push({
                label: 'Extraction'
            });

            breadcrumbs.push({
                label: fileId?.toString() || 'Txn'
            });

            this._breadcrumbs$.next(breadcrumbs);
            return;
        }

        if (currentPageIndex !== -1) {
            // Helper to fetch param after token
            const getAfter = (token: string) => {
                const idx = segments.indexOf(token);
                return idx !== -1 && idx + 1 < segments.length ? segments[idx + 1] : null;
            };

            const clientId =
                getAfter('idp-application') ||
                getAfter('idp-cust') ||
                getAfter('idp-process') ||
                getAfter('idp-doc-grid') ||
                getAfter('idp-extraction') ||
                getAfter('idp-customer-process') ||
                getAfter('document-summary') ||
                getAfter('transaction-logger') ||
                getAfter('process-doc-extraction');

            // First crumb: client name (skip generic 'IDP Client') once app is selected
            if (currentPageIndex >= 1 && clientId) {
                const clientCrumb: Breadcrumb = {
                    label: this.displayNames.clientName || 'Client'
                };

                // Only add URL if not on idp-application route (currentPageIndex !== 1)
                if (currentPageIndex !== 1) {
                    clientCrumb.url = `/extraction/idp-application/${clientId}`;
                }
                breadcrumbs.push(clientCrumb);
            }

            // Build remaining crumbs, skip index 0 (idp-customer)
            // Determine which path user is on: Path A (idp-process/idp-doc-grid/idp-extraction) or Path B (idp-customer-process)
            const onPathB = segments.includes('idp-customer-process');
            const onPathA = (segments.includes('idp-process') || segments.includes('idp-doc-grid') || segments.includes('idp-extraction')) && !onPathB;

            for (let i = 1; i <= currentPageIndex; i++) {
                if (i === 1 && currentPageIndex > 1) continue; // skip idp-application when deeper

                // Path A: skip customer-process (3), transaction-logger (4), document-summary (5), process-doc-extraction (6)
                if (onPathA && (i === 3 || i === 4 || i === 5 || i === 6)) continue;

                // Path B: skip idp-process (7) and idp-doc-grid (8) and idp-extraction (9)
                if (onPathB && (i === 7 || i === 8 || i === 9)) continue;

                const isLast = i === currentPageIndex;
                const path = this.idpWorkflowHierarchy[i].path;
                const label = this.getDynamicLabelForPath(path, isLast);

                // Do not allow Extraction to be clickable when on idp-extraction page
                if (path === 'idp-extraction') {
                    breadcrumbs.push({ label });
                } else {
                    breadcrumbs.push({
                        label
                        // url: this.buildBreadcrumbUrl(i, currentUrl, routeParams, queryParams)
                    });
                }
            }
        } else {
            const root = this.router.routerState.snapshot.root;
            this.addBreadcrumb(root, [], breadcrumbs);
        }

        this._breadcrumbs$.next(this.ensureUrls(breadcrumbs, this.collectRouteParams(), queryParams));
    }

    shouldShowBackButton(): boolean {
        return canGoBack(this.router);
    }

    goBack() {
        navigateBack(this.router);
    }

    private getPrimarySegments(url: string): string[] {
        const tree = this.router.parseUrl(url);
        const primary = tree.root.children['primary'];
        return primary ? primary.segments.map((s) => s.path) : [];
    }

    private buildBreadcrumbUrl(workflowIndex: number, currentUrl: string, params: Record<string, any>, queryParams: Record<string, any>): string {
        const clientId = this.loginService.getClientId();
        const appId = this.loginService.getApplicationId();
        const custId = this.loginService.getCustomerId();
        const docId = this.loginService.getDocumentId();
        const fileId = this.loginService.getTxnId();
        const processType = this.loginService.getProcessId();
        const batchId = params['batchId'] ?? null;

        switch (workflowIndex) {
            case 0: // IDP Customer
                return '/extraction/idp-customer';
            case 1: // IDP Application
                return clientId ? `/extraction/idp-application/${clientId}` : '/extraction/idp-application';
            case 2: // IDP Customer (for a specific app)
                return clientId && appId ? `/extraction/idp-cust/${clientId}/${appId}` : '/extraction/idp-cust';
            case 3: // IDP Customer Process
                return clientId && appId && custId ? `/extraction/idp-customer-process/${clientId}/${appId}/${custId}` : '/extraction/idp-customer-process';
            case 4: // Transaction Logger
                return clientId && appId && custId && processType ? `/extraction/transaction-logger/${clientId}/${appId}/${custId}/${processType}` : '/extraction/transaction-logger';
            case 5: // Document Summary
                return clientId && appId && custId && processType ? `/extraction/document-summary/${clientId}/${appId}/${custId}/${processType}` : '/extraction/document-summary';
            case 6: // Process Doc Extraction (Administration path)
                return clientId && appId && custId && processType && batchId ? `/home/administration/process-doc-extraction/${clientId}/${appId}/${custId}/${processType}/${batchId}` : '/home/administration/process-doc-extraction';
            case 7: // IDP Process
                return clientId && appId && custId ? `/extraction/idp-process/${clientId}/${appId}/${custId}` : '/extraction/idp-process';
            case 8: // IDP Document List
                return clientId && appId && custId && docId ? `/extraction/idp-doc-grid/${clientId}/${appId}/${custId}/${docId}` : '/extraction/idp-doc-grid';
            case 9: // IDP Extraction
                return clientId && appId && custId && docId && fileId ? `/extraction/idp-extraction/${clientId}/${appId}/${custId}/${docId}/${fileId}` : '/extraction/idp-extraction';
            default:
                return '/extraction';
        }
    }

    private collectRouteParams(): Record<string, any> {
        const params: Record<string, any> = {};
        const walk = (route: ActivatedRouteSnapshot | null) => {
            if (!route) return;
            Object.assign(params, route.params || {});
            if (route.firstChild) walk(route.firstChild);
        };
        walk(this.router.routerState.snapshot.root);
        return params;
    }

    private ensureUrls(items: Breadcrumb[], params: Record<string, any>, queryParams: Record<string, any>): Breadcrumb[] {
        const clientId = this.loginService.getClientId();
        const appId = this.loginService.getApplicationId();
        const custId = this.loginService.getCustomerId();
        const docType = this.loginService.getDocumentId();

        return items.map((item, idx, arr) => {
            const last = idx === arr.length - 1;
            if (item.url || last) return item;

            const label = (item.label || '').toLowerCase();
            let url: string | null = null;

            if (clientId && (label.includes('application') || label.includes('client'))) {
                url = `/extraction/idp-application/${clientId}`;
            } else if (clientId && appId && (label.includes('customer') || label.includes('customers'))) {
                url = `/extraction/idp-cust/${clientId}/${appId}`;
            } else if (clientId && appId && custId && (label.includes('document type') || label.includes('document types') || label.includes('process'))) {
                url = `/extraction/idp-process/${clientId}/${appId}/${custId}`;
            } else if (clientId && appId && custId && docType && (label.includes('file') || label.includes('files'))) {
                url = `/extraction/idp-doc-grid/${clientId}/${appId}/${custId}/${docType}`;
            }

            return url ? { ...item, url } : item;
        });
    }

    private addBreadcrumb(route: ActivatedRouteSnapshot, parentUrl: string[], breadcrumbs: Breadcrumb[]) {
        const routeUrl = parentUrl.concat(route.url.map((url) => url.path));
        const breadcrumb = route.data['breadcrumb'];
        const parentBreadcrumb = route.parent && route.parent.data ? route.parent.data['breadcrumb'] : null;

        if (breadcrumb && breadcrumb !== parentBreadcrumb) {
            breadcrumbs.push({
                label: route.data['breadcrumb'],
                url: '/' + routeUrl.join('/')
            });
        }

        if (route.firstChild) {
            this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
        }
    }

    private getDeepestRouteLabel(route: ActivatedRouteSnapshot): string | null {
        let node: ActivatedRouteSnapshot | null = route;
        while (node && node.firstChild) {
            node = node.firstChild;
        }
        return node && node.data ? (node.data['breadcrumb'] as string) : null;
    }

    private async populateDisplayNames(segments: string[], queryParams: Record<string, any>) {
        // Prefer router snapshot params for reliability, fall back to segment offsets
        const collectedParams: Record<string, any> = {};
        const collectParams = (route: ActivatedRouteSnapshot | null) => {
            if (!route) return;
            Object.assign(collectedParams, route.params || {});
            if (route.firstChild) collectParams(route.firstChild);
        };
        collectParams(this.router.routerState.snapshot.root);

        const getAfter = (token: string, offset: number = 1) => {
            const idx = segments.indexOf(token);
            const targetIndex = idx !== -1 ? idx + offset : -1;
            return targetIndex !== -1 && targetIndex < segments.length ? segments[targetIndex] : null;
        };

        let clientId = collectedParams['client_id'] ?? (getAfter('idp-application', 1) || getAfter('idp-cust', 1) || getAfter('idp-process', 1) || getAfter('idp-doc-grid', 1) || getAfter('idp-extraction', 1) || getAfter('idp-customer-process', 1));
        let appId = collectedParams['appId'] ?? (getAfter('idp-cust', 2) || getAfter('idp-process', 2) || getAfter('idp-doc-grid', 2) || getAfter('idp-extraction', 2) || getAfter('idp-customer-process', 2));
        let custId = collectedParams['custId'] ?? (getAfter('idp-process', 3) || getAfter('idp-doc-grid', 3) || getAfter('idp-extraction', 3) || getAfter('idp-customer-process', 3));
        const docTypeFromQuery = queryParams['type'] as string | undefined;
        // docId may be part of route params in idp-doc-grid or idp-extraction
        const docIdParam = collectedParams['docId'] ?? (getAfter('idp-doc-grid', 4) || getAfter('idp-extraction', 4));
        const processType = collectedParams['processType'] as string | undefined;

        // Only fall back to stored context inside the extraction workflow.
        // For administration/masterdata/home pages we should trust the current route,
        // otherwise an old selected client can keep showing up in breadcrumbs after exit.
        if (this.shouldUseStoredContext(segments)) {
            const lsClientId = this.loginService.getClientId();
            const lsAppId = this.loginService.getApplicationId();
            const lsCustId = this.loginService.getCustomerId();

            if (!clientId && lsClientId) clientId = lsClientId.toString();
            if (!appId && lsAppId) appId = lsAppId.toString();
            if (!custId && lsCustId) custId = lsCustId.toString();
        }

        const tasks: Promise<void>[] = [];

        if (clientId && !this.displayNames.clientName) {
            tasks.push(
                firstValueFrom(this.breadcrumbService.getClientName(Number(clientId)))
                    .then((name: string | null) => {
                        this.displayNames.clientName = name;
                    })
                    .catch(() => {})
            );
        }

        // if (clientId && appId && !this.displayNames.applicationName) {
        //     tasks.push(
        //         firstValueFrom(this.breadcrumbService.getApplicationName(Number(clientId), Number(appId)))
        //             .then((name: string | null) => {
        //                 this.displayNames.applicationName = name;
        //             })
        //             .catch(() => { })
        //     );
        // }

        // if (clientId && custId && !this.displayNames.customerName) {
        //     tasks.push(
        //         firstValueFrom(this.breadcrumbService.getCustomerName(Number(clientId), Number(custId)))
        //             .then((name: string | null) => {
        //                 this.displayNames.customerName = name;
        //             })
        //             .catch(() => { })
        //     );
        // }

        if (docTypeFromQuery && !this.displayNames.documentType) {
            this.displayNames.documentType = docTypeFromQuery;
        }
        // Fallback: if no document type string is present, derive from docId
        if (!this.displayNames.documentType && docIdParam) {
            const name = this.getDocTypeById(Number(docIdParam));
            this.displayNames.documentType = name || null;
        }

        if (processType && !this.displayNames.processName) {
            this.displayNames.processName = getProcessTypeLabel(processType);
        }

        if (tasks.length) {
            await Promise.all(tasks);
        }
    }

    private shouldUseStoredContext(segments: string[]): boolean {
        return ['idp-application', 'idp-cust', 'idp-customer-process', 'transaction-logger', 'document-summary', 'process-doc-extraction', 'idp-process', 'idp-doc-grid', 'idp-extraction'].some((segment) => segments.includes(segment));
    }

    private getDynamicLabelForPath(path: string, isLast: boolean): string {
        switch (path) {
            case 'idp-application':
                return isLast ? (this.displayNames.clientName ? `${this.displayNames.clientName} Applications` : 'Applications') : this.displayNames.applicationName || 'Application';
            case 'idp-cust':
                return isLast ? (this.displayNames.applicationName ? `${this.displayNames.applicationName} Customers` : 'Customers') : this.displayNames.applicationName || 'Application';
            case 'idp-customer-process':
                return isLast ? (this.displayNames.customerName ? `${this.displayNames.customerName} Processes` : 'Processes') : this.displayNames.customerName || 'Customer';
            case 'transaction-logger':
                return this.displayNames.processName ? `${this.displayNames.processName} Transactions` : 'Transactions';
            case 'idp-extraction':
                const txnId = this.loginService.getTxnId();
                return txnId ? txnId.toString() : 'Extraction';
            case 'document-summary':
                return 'Document Summary';
            case 'process-doc-extraction':
                return 'Extraction';
            case 'idp-process':
                return isLast ? (this.displayNames.customerName ? `${this.displayNames.customerName} Document Types` : 'Document Types') : this.displayNames.customerName || 'Customer';
            case 'idp-doc-grid':
                // Use document type if known; else try to derive from docId param
                if (!this.displayNames.documentType) {
                    const params = this.collectRouteParams();
                    const docId = params['docId'];
                    if (docId) {
                        const derived = this.getDocTypeById(Number(docId));
                        if (derived && derived !== '--') {
                            return isLast ? `${derived} Files` : derived;
                        }
                    }
                }
                return isLast ? (this.displayNames.documentType ? `${this.displayNames.documentType} Files` : 'Files') : this.displayNames.documentType || 'Files';
            default:
                return this.idpWorkflowHierarchy.find((item) => item.path === path)?.label || path;
        }
    }

    getDocTypeById(id: number) {
        switch (id) {
            case 5:
                return 'Invoice';
            case 10:
                return 'Packing List';
            case 15:
                return 'Bill of Lading';
            case 20:
                return 'Death Certificate';
            case 25:
                return 'Transport Instruction';
            case 79:
                return 'Transmittal Letter';
            default:
                return '--';
        }
    }
}
